import { createEntityMetadata } from "../domain/common";
import type {
  CEOReviewExport,
  CEOReviewExportPayload,
  EvidenceStatement,
  ExternalSummaryRecord,
  StructuredCEORecord,
} from "../domain/types";
import { createCEOReviewExport } from "../contracts/envelope";
import { containsForbiddenPrivateField } from "../privacy/policy";
import { assertNoIssues, RuleErrorCode } from "../rules/errors";
import { validateEvidenceStatement, validateExternalSummary } from "../rules/invariants";

export type ReviewDeviationField =
  | "uncomfortable_truth"
  | "focus_deviation"
  | "repetition_failure"
  | "trust_deviation"
  | "decision_quality_issue"
  | "delegation_issue"
  | "risk_issue"
  | "energy_issue"
  | "field_adaptation_issue";

export interface BoardFieldSelection {
  structuredRecordId: string;
  summary: string;
}

export interface BoardSummaryDraft {
  reviewId: string;
  date: string;
  fields: Partial<Record<ReviewDeviationField, BoardFieldSelection | null>>;
  correctionId: string;
  confidence: number;
  includeFields: ReviewDeviationField[];
}

function confirmedExportableRecord(
  record: StructuredCEORecord | undefined,
): record is StructuredCEORecord {
  return (
    record !== undefined &&
    record.exportAllowed &&
    record.deletionState === "active" &&
    (record.userConfirmation === "confirmed" || record.userConfirmation === "corrected")
  );
}

export async function projectCEOReviewExport(
  draft: BoardSummaryDraft,
  records: readonly StructuredCEORecord[],
  now = new Date().toISOString(),
): Promise<{ envelope: CEOReviewExport; summary: ExternalSummaryRecord }> {
  const byId = new Map(records.map((record) => [record.id, record]));
  const selectedSourceIds = new Set<string>();
  const getValue = (field: ReviewDeviationField): string | null => {
    if (!draft.includeFields.includes(field)) {
      return null;
    }
    const selection = draft.fields[field];
    if (!selection) {
      return null;
    }
    const source = byId.get(selection.structuredRecordId);
    if (!confirmedExportableRecord(source)) {
      return null;
    }
    selectedSourceIds.add(source.id);
    if (source.recordType === "daily_review") {
      for (const statementId of source.statementIds) {
        const statement = byId.get(statementId);
        if (
          statement?.recordType === "evidence_statement" &&
          statement.content === selection.summary
        ) {
          selectedSourceIds.add(statementId);
        }
      }
    }
    return selection.summary;
  };

  const correction = byId.get(draft.correctionId);
  if (!confirmedExportableRecord(correction) || correction.recordType !== "behavior_correction") {
    throw new Error("CEO-RULE-014: 董事会摘要必须引用用户确认的唯一行为纠偏。");
  }
  const behaviorCorrection = correction;
  selectedSourceIds.add(behaviorCorrection.id);

  const evidenceStatements = records.filter(
    (record): record is EvidenceStatement =>
      record.recordType === "evidence_statement" &&
      confirmedExportableRecord(record) &&
      (record.statementType === "fact" || record.statementType === "inference"),
  );
  for (const statement of evidenceStatements) {
    assertNoIssues(
      validateEvidenceStatement(statement, {
        statementsById: new Map(evidenceStatements.map((item) => [item.id, item])),
        forExternalExport: true,
      }),
    );
  }

  const payload: CEOReviewExportPayload = {
    review_id: draft.reviewId,
    date: draft.date,
    uncomfortable_truth: getValue("uncomfortable_truth"),
    focus_deviation: getValue("focus_deviation"),
    repetition_failure: getValue("repetition_failure"),
    trust_deviation: getValue("trust_deviation"),
    decision_quality_issue: getValue("decision_quality_issue"),
    delegation_issue: getValue("delegation_issue"),
    risk_issue: getValue("risk_issue"),
    energy_issue: getValue("energy_issue"),
    field_adaptation_issue: getValue("field_adaptation_issue"),
    tomorrow_behavior_correction: behaviorCorrection.action,
    confidence: draft.confidence,
    evidence: evidenceStatements
      .filter((statement) => selectedSourceIds.has(statement.id))
      .map((statement) => ({
        evidence_id: statement.id,
        kind: "user_observation" as const,
        statement: statement.content,
        source_record_id: statement.id,
        export_approved: true as const,
      })),
  };
  if (containsForbiddenPrivateField(payload)) {
    throw new Error(`${RuleErrorCode.PRIVATE_TEXT_IN_EXTERNAL_SUMMARY}: 导出投影包含禁用字段。`);
  }
  const envelope = await createCEOReviewExport(payload, now);
  const summary: ExternalSummaryRecord = {
    ...createEntityMetadata(
      { kind: "derived", referenceIds: [...selectedSourceIds] },
      { now, status: "confirmed", exportAllowed: true },
    ),
    summaryType: "ceo_review_export",
    date: draft.date,
    sourceStructuredRecordIds: [...selectedSourceIds],
    source: {
      kind: "derived",
      referenceIds: [...selectedSourceIds],
    },
    isIndependentFactSource: false,
    payload,
  };
  assertNoIssues(validateExternalSummary(summary, new Set()));
  return { envelope, summary };
}
